import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MODULE_LABELS,
  PLAN_CHANGE_STATUS_LABELS,
  type Entitlements,
  type LimitKey,
  type Plan,
  type PlanChangeRequest,
} from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { formatCurrency } from '../lib/format.js';
import { useSession } from '../lib/session.js';
import {
  Alert,
  Button,
  Card,
  CONTROL_CLASS,
  PageHeader,
  StatCard,
  StatusBadge,
  cx,
} from '../components/ui.js';

/**
 * Tela de plano e consumo.
 *
 * E aqui que o modelo de entitlements aparece para o cliente: o que ele tem,
 * quanto ja usou e o que ganharia subindo de plano. A barra de consumo e o
 * gatilho natural de upgrade — por isso ela vem antes do catalogo.
 *
 * O catalogo terminava num botao "Falar com vendas" desabilitado, e era ali
 * que o caminho de receita morria. Agora o sindico SOLICITA a troca e a Azko
 * decide: pedido, nao troca. Upgrade autoaplicado cobraria mais sem ninguem
 * confirmar, e downgrade autoaplicado cortaria modulo em uso no meio do
 * expediente da portaria.
 */

const TRACKED_LIMITS: Array<{ key: LimitKey; label: string }> = [
  { key: 'packages.per_month', label: 'Encomendas neste mes' },
  { key: 'core.units.max', label: 'Unidades' },
  { key: 'core.residents.max', label: 'Moradores' },
  { key: 'core.users.max', label: 'Usuarios' },
];

interface PlanChangeState {
  pending: PlanChangeRequest | null;
  history: PlanChangeRequest[];
}

function UsageBar({ used, limit }: { used: number; limit: number | null }) {
  if (limit === null) {
    return <p className="mt-2 text-sm text-accent">Ilimitado</p>;
  }

  const share = limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const nearLimit = share >= 80;

  return (
    <div className="mt-3">
      <div className="h-2 overflow-hidden rounded-pill bg-surface-2">
        <div
          className={cx('h-full rounded-pill', nearLimit ? 'bg-warn' : 'bg-accent')}
          style={{ width: `${share}%` }}
        />
      </div>
      <p className="mt-2 text-sm text-muted">
        {used} de {limit} ({share}%)
      </p>
    </div>
  );
}

export function PlanPage() {
  const { entitlements, allows } = useSession();
  const queryClient = useQueryClient();

  /** Qual card abriu o formulario de solicitacao. */
  const [asking, setAsking] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const podeSolicitar = allows('condominio:assinatura:solicitar_troca');

  const { data: subscription } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api.get<Entitlements>('/billing/subscription'),
  });

  const { data: catalog } = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => api.get<{ items: Plan[] }>('/billing/plans'),
  });

  const { data: planChange } = useQuery({
    queryKey: ['billing', 'plan-change'],
    queryFn: () => api.get<PlanChangeState>('/billing/plan-change'),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['billing'] });
  }

  const request = useMutation({
    mutationFn: (planCode: string) =>
      api.post<PlanChangeRequest>('/billing/plan-change', {
        planCode,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      setAsking(null);
      setNote('');
      setError(null);
      refresh();
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiRequestError ? cause.message : 'Falha ao solicitar a troca.',
      );
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.delete('/billing/plan-change'),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiRequestError ? cause.message : 'Falha ao cancelar o pedido.',
      );
    },
  });

  const current = subscription ?? entitlements;
  const pending = planChange?.pending ?? null;
  // Solicitacoes ja respondidas — a pendente aparece no card de cima.
  const history = (planChange?.history ?? []).filter((item) => item.status !== 'pending');

  return (
    <>
      <PageHeader title="Plano" subtitle="Consumo do periodo e o que muda em cada plano." />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Plano atual" value={current?.planName ?? '—'} highlight />
        <StatCard
          label="Status"
          value={current?.status ?? '—'}
          hint={
            current?.currentPeriodEnd
              ? `renova em ${new Date(current.currentPeriodEnd).toLocaleDateString('pt-BR')}`
              : undefined
          }
        />
        <StatCard label="Modulos ativos" value={current?.modules.length ?? '—'} />
        <StatCard
          label="Encomendas no mes"
          value={current?.usage['packages.per_month'] ?? 0}
          hint={
            current?.limits['packages.per_month'] === null
              ? 'sem limite'
              : `limite ${current?.limits['packages.per_month'] ?? 0}`
          }
        />
      </div>

      {error && <Alert>{error}</Alert>}

      {/* A pendencia vem antes de tudo: enquanto ela existe, o catalogo abaixo
          nao aceita pedido novo, e o sindico precisa saber por que. */}
      {pending && (
        <Card className="border-accent/40 bg-accent/5 p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Troca solicitada</p>
              <h2 className="mt-2 text-xl font-bold tracking-tight">
                {pending.currentPlanCode ?? 'Sem plano'} → {pending.requestedPlanCode}
              </h2>
              <p className="mt-2 text-sm text-muted">
                Pedido em{' '}
                {new Date(pending.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                })}
                {pending.requestedByName && ` por ${pending.requestedByName}`}. A equipe
                Azko responde por aqui.
              </p>
              {pending.note && (
                <p className="mt-2 text-sm text-faint">"{pending.note}"</p>
              )}
            </div>
            <StatusBadge tone="accent">
              {PLAN_CHANGE_STATUS_LABELS[pending.status]}
            </StatusBadge>
          </div>

          {podeSolicitar && (
            <Button
              variant="ghost"
              className="mt-5"
              loading={cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              Cancelar pedido
            </Button>
          )}
        </Card>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight">Consumo</h2>
          <div className="mt-6 space-y-6">
            {current &&
              TRACKED_LIMITS.map(({ key, label }) => (
                <div key={key}>
                  <p className="font-semibold">{label}</p>
                  {/* `?? null`, e nao `?? 0`: no contrato dos entitlements,
                      `null` E o valor de "ilimitado". Com `?? 0` o plano sem
                      teto aparecia como "24 de 0 (0%)" — a barra dizia que o
                      cliente estourou um limite que ele nao tem, no lugar onde
                      ele vai olhar para decidir se faz upgrade. */}
                  <UsageBar used={current.usage[key] ?? 0} limit={current.limits[key] ?? null} />
                </div>
              ))}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight">Modulos</h2>
          <ul className="mt-6 space-y-3">
            {(Object.keys(MODULE_LABELS) as Array<keyof typeof MODULE_LABELS>).map((code) => {
              const active = current?.modules.includes(code) ?? false;
              return (
                <li key={code} className="flex items-center justify-between gap-3">
                  <span className={active ? 'font-semibold' : 'text-faint'}>
                    {MODULE_LABELS[code]}
                  </span>
                  <span className={cx('text-sm', active ? 'text-accent' : 'text-faint')}>
                    {active ? 'ativo' : 'nao contratado'}
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {catalog?.items.map((plan) => {
          const isCurrent = plan.code === current?.planCode;
          const asked = pending?.requestedPlanCode === plan.code;
          const open = asking === plan.code;

          return (
            <Card
              key={plan.code}
              className={cx('p-6', isCurrent && 'border-accent/40 bg-accent/5')}
            >
              <p className="eyebrow">{isCurrent ? 'Plano atual' : 'Plano'}</p>
              <h3 className="mt-2 text-2xl font-bold tracking-tight">{plan.name}</h3>
              <p className="mt-1 text-sm text-muted">{plan.description}</p>
              <p className="mt-4 text-3xl font-extrabold tracking-tight">
                {formatCurrency(plan.priceCents, plan.currency)}
                <span className="text-base font-semibold text-muted">/mes</span>
              </p>

              <ul className="mt-5 space-y-2 text-sm text-muted">
                {plan.modules
                  .filter((code) => code !== 'core')
                  .map((code) => (
                    <li key={code}>• {MODULE_LABELS[code]}</li>
                  ))}
              </ul>

              {open ? (
                <div className="mt-6">
                  <label className="block text-sm font-semibold text-muted" htmlFor={`note-${plan.code}`}>
                    Por que a troca? (opcional)
                  </label>
                  <textarea
                    id={`note-${plan.code}`}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    rows={3}
                    maxLength={255}
                    placeholder="Ajuda quem vai analisar do lado da Azko."
                    className={cx(CONTROL_CLASS, 'mt-1.5 resize-none')}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      loading={request.isPending}
                      onClick={() => request.mutate(plan.code)}
                    >
                      Enviar pedido
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setAsking(null);
                        setNote('');
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant={isCurrent ? 'ghost' : 'primary'}
                  className="mt-6 w-full"
                  // Enquanto houver pendencia, nenhum outro plano aceita pedido:
                  // o banco so guarda uma solicitacao aberta por condominio, e
                  // um botao que sempre responde 409 seria uma armadilha.
                  disabled={isCurrent || !podeSolicitar || pending !== null}
                  onClick={() => {
                    setAsking(plan.code);
                    setNote('');
                    setError(null);
                  }}
                >
                  {isCurrent
                    ? 'Plano atual'
                    : asked
                      ? 'Pedido enviado'
                      : pending
                        ? 'Ha um pedido em aberto'
                        : podeSolicitar
                          ? 'Solicitar este plano'
                          : 'Fale com o sindico'}
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {history.length > 0 && (
        <Card className="p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight">Pedidos anteriores</h2>
          <ul className="mt-5 space-y-4">
            {history.map((item) => (
              <li key={item.id} className="border-b border-line-soft pb-4 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">
                    {item.currentPlanCode ?? 'Sem plano'} → {item.requestedPlanCode}
                  </span>
                  <span className="text-sm text-muted">
                    {PLAN_CHANGE_STATUS_LABELS[item.status]}
                    {item.decidedAt &&
                      ` • ${new Date(item.decidedAt).toLocaleDateString('pt-BR')}`}
                  </span>
                </div>
                {/* O motivo da recusa e a unica coisa que diz ao sindico o que
                    fazer a seguir — sem ele, o pedido negado vira mistério. */}
                {item.decisionReason && (
                  <p className="mt-1 text-sm text-faint">{item.decisionReason}</p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
